import React, {useEffect, useState, useRef} from 'react';
import {Modal, Button} from "react-bootstrap";
import {useTranslation} from "react-i18next";
import {requestAcceptOffer, requestGetOffer} from "../../api";
import {OfferVersionResponse} from "../../models";
import Checkbox from "../../../../common/components/Checkbox";
import Loader from "../../../../common/components/Loader";
import {getAxiosErrorMessage} from "../../../../common/utils/functions";
import {addNotify} from "../../../../common/actions/notify";
import {clearNeedAcceptOffer} from "../../../../common/actions/appStatus";
import {useDispatch} from "react-redux";
import styles from "./OfferModal.module.scss";

type OfferModalProps = {
  show: boolean;
  onAccepted: () => void;
  onRemindLater: () => void;
  onClose: () => void;
  viewOnly?: boolean;
}

const LANGUAGES = [
  {code: 'ru' as const, label: 'RU'},
  {code: 'kk' as const, label: 'KK'},
];

/**
 * Модальное окно принятия/просмотра офферты
 */
const OfferModal: React.VFC<OfferModalProps> = ({
  show,
  onAccepted,
  onRemindLater,
  onClose,
  viewOnly = false,
}) => {
  const {t} = useTranslation();
  const dispatch = useDispatch();
  const dropdownRef = useRef<HTMLDivElement>(null);

  const [offer, setOffer] = useState<OfferVersionResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [accepting, setAccepting] = useState(false);
  const [isChecked, setIsChecked] = useState(true);
  const [lang, setLang] = useState<'ru' | 'kk'>('ru');
  const [isLangDropdownOpen, setIsLangDropdownOpen] = useState(false);

  // Показывать только кнопку "Закрыть" если режим просмотра
  const showOnlyCloseButton = viewOnly;

  const loadOffer = (language: 'ru' | 'kk') => {
    setLoading(true);
    requestGetOffer(language)
      .then(({data}) => {
        setOffer(data);
      })
      .catch(err => {
        dispatch(addNotify(getAxiosErrorMessage(err)));
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    if (show && !offer) {
      loadOffer(lang);
    }
  }, [show]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsLangDropdownOpen(false);
      }
    };

    if (isLangDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isLangDropdownOpen]);

  const handleLanguageChange = (newLang: 'ru' | 'kk') => {
    setIsLangDropdownOpen(false);
    if (newLang !== lang) {
      setLang(newLang);
      loadOffer(newLang);
    }
  };

  const currentLangLabel = LANGUAGES.find(l => l.code === lang)?.label || 'RU';

  const formatOfferDate = (timestamp: number): string => {
    const date = new Date(timestamp * 1000);
    return date.toLocaleDateString('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const handleAccept = () => {
    setAccepting(true);
    requestAcceptOffer()
      .then(() => {
        dispatch(clearNeedAcceptOffer());
        onAccepted();
      })
      .catch(err => {
        dispatch(addNotify(getAxiosErrorMessage(err)));
      })
      .finally(() => {
        setAccepting(false);
      });
  };

  const handleHide = () => {
    if (showOnlyCloseButton) {
      onClose();
    } else {
      onRemindLater();
    }
  };

  const modalTitle = viewOnly
    ? t('offer.modalTitleView')
    : t('offer.modalTitle');

  return (
    <Modal show={show} centered onHide={handleHide} className={styles.modal}>
      <Modal.Header closeButton>
        <Modal.Title>{modalTitle}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {loading ? (
          <div className="d-flex justify-content-center py-5">
            <Loader/>
          </div>
        ) : offer ? (
          <>
            {offer.changes && !viewOnly && (
              <div className={styles.changesSection}>
                <div className={styles.changesTitle}>{t('offer.changesTitle')}</div>
                <div dangerouslySetInnerHTML={{__html: offer.changes}} />
              </div>
            )}
            <div className={styles.offerTextWrapper}>
              <div className={styles.offerHeader}>
                <span className={styles.offerVersion}>
                  {t('offer.offerVersion', {date: formatOfferDate(offer.startedAt)})}
                </span>
                <div className={styles.langSelector} ref={dropdownRef}>
                  <button
                    type="button"
                    className={styles.langDropdown}
                    onClick={() => setIsLangDropdownOpen(!isLangDropdownOpen)}
                  >
                    {currentLangLabel}
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <g clipPath="url(#clip0_103_5337)">
                        <path d="M13.825 7.1582L10 10.9749L6.175 7.1582L5 8.3332L10 13.3332L15 8.3332L13.825 7.1582Z" fill="black"/>
                      </g>
                      <defs>
                        <clipPath id="clip0_103_5337">
                          <rect width="20" height="20" fill="white"/>
                        </clipPath>
                      </defs>
                    </svg>
                  </button>
                  {isLangDropdownOpen && (
                    <div className={styles.langDropdownMenu}>
                      {LANGUAGES.map((language) => (
                        <button
                          key={language.code}
                          type="button"
                          className={`${styles.langOption} ${lang === language.code ? styles.active : ''}`}
                          onClick={() => handleLanguageChange(language.code)}
                        >
                          {language.label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              <div
                className={styles.offerText}
                dangerouslySetInnerHTML={{__html: offer.text}}
              />
            </div>
            {!showOnlyCloseButton && (
              <div className={styles.checkboxWrapper}>
                <Checkbox
                  id="offer-accept-checkbox"
                  checked={isChecked}
                  onChange={(e) => setIsChecked(e.target.checked)}
                  label={t('offer.checkboxLabel')}
                />
              </div>
            )}
          </>
        ) : null}
      </Modal.Body>
      <Modal.Footer className="justify-content-center">
        {showOnlyCloseButton ? (
          <Button
            variant="outline-secondary"
            onClick={onClose}
          >
            {t('common.actionClose')}
          </Button>
        ) : (
          <>
            <Button
              variant="outline-secondary"
              onClick={onRemindLater}
              disabled={accepting}
            >
              {t('offer.remindLater')}
            </Button>
            <Button
              variant="outline-primary"
              onClick={handleAccept}
              disabled={!isChecked || accepting}
            >
              {t('offer.acceptButton')}
            </Button>
          </>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default OfferModal;
